import React, { FC, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import geographyStates from '../../../../constants/geographyStates';

type Option = { code: string; name: string };

const STATES: Option[] = Object.entries(geographyStates).map(
  ([name, code]) => ({ name, code: code as string }),
);

type Props = {
  register: any;
  errors: any;
  setValue: Function;
  control: any;
  required?: boolean;
};

const findOption = (code: string | undefined): Option | null => {
  if (!code) return null;
  return STATES.find(s => s.code === code) || null;
};

const SearchableStatesDropdown: FC<Props> = ({
  register,
  errors,
  setValue,
  control,
  required,
}) => {
  const hasError = errors.state !== undefined;
  const [selected, setSelected] = useState<Option | null>(null);

  useEffect(() => {
    register('state');
  }, [register]);

  return (
    <FieldWrapper>
      <Label $error={hasError}>
        State
        {required && <Asterisk>*</Asterisk>}
      </Label>
      <Controller
        name="state"
        control={control}
        defaultValue=""
        render={({ value }) => {
          const current = selected || findOption(value);
          return (
            <StyledAutocomplete
              options={STATES}
              value={current}
              getOptionLabel={(option: any) => (option ? option.name : '')}
              getOptionSelected={(option: any, val: any) =>
                option && val ? option.code === val.code : false
              }
              onChange={(_, val: any) => {
                setSelected(val);
                setValue('state', val ? val.code : '');
              }}
              autoHighlight
              openOnFocus
              renderOption={(option: any) => (
                <OptionRow>
                  <OptionName>{option.name}</OptionName>
                  <OptionCode>{option.code}</OptionCode>
                </OptionRow>
              )}
              renderInput={params => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  variant="outlined"
                  placeholder="Search states…"
                  error={hasError}
                />
              )}
            />
          );
        }}
      />
      {hasError && <ErrorMessage>{errors.state.message}</ErrorMessage>}
    </FieldWrapper>
  );
};

export default SearchableStatesDropdown;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: inherit;
  min-width: 0;
`;

const Label = styled.label<{ $error: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ $error }) =>
    ($error ? 'var(--danger-500, #dc2626)' : 'var(--mainText)')};
  margin-bottom: 6px;
`;

const Asterisk = styled.span`
  color: var(--danger-500, #dc2626);
  margin-left: 2px;
`;

const StyledAutocomplete = styled(Autocomplete)`
  && {
    width: 100%;

    .MuiOutlinedInput-root {
      padding: 0 !important;
      height: 40px;
      border-radius: 8px;
      background: var(--neutral-0);
      font-size: 0.875rem;
    }

    .MuiOutlinedInput-input {
      padding: 0 12px !important;
      height: 40px;
      box-sizing: border-box;
    }

    .MuiAutocomplete-endAdornment {
      right: 6px;
    }

    .MuiOutlinedInput-notchedOutline {
      border-color: var(--border) !important;
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: var(--neutral-400) !important;
    }

    .Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: var(--primary-600) !important;
      box-shadow: var(--shadow-focus);
    }
  }
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const OptionName = styled.span`
  font-size: 0.875rem;
  color: var(--mainText);
`;

const OptionCode = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--neutral-500);
`;

const ErrorMessage = styled.div`
  font-size: 0.75rem;
  color: var(--danger-500, #dc2626);
  margin-top: 4px;
`;
